import { useEffect, useRef, useState } from 'react';
import Api from '../api/Api';
import {
  closeConnection,
  createConnection,
  negotiateSignalR,
  registerOnMessageReceived,
  startConnection,
  unregisterOnMessageReceived,
} from '../services/signalRService';
import useAuthStore from '../store/authStore';
import { WaterdataGroup } from '../types/userGroupPermissions';

/**
 *
 * @param value Returns the previous value and store the current for next time.
 */
export function usePrevious<T>(value: T): T {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref: any = useRef<T | undefined>(undefined);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export const useIsMounted = () => {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return mounted;
};

export const useSubscriptionStatus = () => {
  const user = useAuthStore((state) => state.user);

  const isActiveSubscriber = () => {
    if (!user) return false;
    if (user.waterdataGroup === WaterdataGroup.MOOD_Admin) return true;
    if (!user.subscriptionExpiryDate) return false;

    const subscriptionExpiryDate = new Date(user.subscriptionExpiryDate);
    const currentDate = new Date();

    return subscriptionExpiryDate > currentDate;
  };

  return {
    isActiveSubscriber: isActiveSubscriber(),
  };
};

export const useIsTnw = (datasetId: string) => {
  const [isTnw, setIsTnw] = useState<boolean>(false);

  useEffect(() => {
    setIsTnw(datasetId.toLowerCase().includes('tnw'));
  }, [datasetId]);

  return isTnw;
};

export const useIsDwf = (datasetId: string) => {
  const [isDwf, setIsDwf] = useState<boolean>(false);

  useEffect(() => {
    setIsDwf(datasetId.toLowerCase().includes('dwf'));
  }, [datasetId]);

  return isDwf;
};

export const useSignalR = (messageType: string, messageString: string) => {
  const [messageReceived, setMessageReceived] = useState<boolean>(false);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [receivedMessageText, setReceivedMessageText] = useState<string>('');

  const user = useAuthStore((state) => state.user);

  const setupConn = async () => {
    if (connection) {
      unregisterOnMessageReceived(connection, messageType);
    }

    const negotiateData = await negotiateSignalR(Api.SignalRServer);
    const conn = createConnection(negotiateData.url, () => negotiateData.accessToken);
    await startConnection(conn);
    setConnection(conn);
  };

  const disconnect = async () => {
    if (connection) {
      closeConnection(connection);
    }
  };

  useEffect(() => {
    if (!connection || connection.state !== 'Connected') {
      setupConn();
    }

    return () => {
      disconnect();
    };
  }, [connection?.state]);

  useEffect(() => {
    if (connection) {
      unregisterOnMessageReceived(connection, messageType);

      registerOnMessageReceived(connection, messageType, (signalRUser, message) => {
        if (message.includes(messageString) && user.userName === signalRUser) {
          setReceivedMessageText(message);
          setMessageReceived(true);
        }
      });
    }
  }, [connection, user]);

  const resetMessageState = () => {
    setReceivedMessageText('');
    setMessageReceived(false);
  };

  return { messageReceived, receivedMessageText, resetMessageState };
};
